
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { enhancedDocumentStorage, EnhancedDocument } from '@/services/storage/enhancedDocumentStorage';

interface AddDocumentParams {
  name: string;
  description: string;
  category: string;
  file: File;
  studentId?: string;
  studentName?: string;
}

export const useDocuments = () => {
  const [documents, setDocuments] = useState<EnhancedDocument[]>([]);
  const [loading, setLoading] = useState(true);

  // Enhanced document loading with persistence verification
  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      console.log('📁 Loading documents with enhanced persistence check...');
      
      const storedDocuments = await enhancedDocumentStorage.loadDocuments();
      console.log(`📁 Loaded ${storedDocuments.length} documents with enhanced persistence`);
      
      setDocuments(storedDocuments);
      
      // Enhanced verification logging
      storedDocuments.forEach(doc => {
        if (doc.studentId && doc.studentName) {
          console.log(`📄 Persistent document "${doc.name}" → ${doc.studentName} (ID: ${doc.studentId})`);
        }
        
        // Verify image data integrity
        if (doc.fileType.startsWith('image/') && doc.fileData) {
          console.log(`🖼️ Image document verified: ${doc.name} (${doc.fileData.length} chars)`);
        }
      });
      
    } catch (error) {
      console.error('Enhanced document loading error:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Force refresh with enhanced persistence check
  const refreshDocuments = useCallback(async () => {
    console.log('🔄 Force refreshing with enhanced persistence...');
    await loadDocuments();
  }, [loadDocuments]);

  // Convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  // Enhanced document addition with immediate persistence
  const addDocument = useCallback(async (params: AddDocumentParams) => {
    try {
      console.log(`📤 Adding document with enhanced persistence: ${params.name}`);
      
      const fileData = await fileToBase64(params.file);
      
      const newDocument: EnhancedDocument = {
        id: `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: params.name,
        description: params.description,
        category: params.category,
        fileName: params.file.name,
        fileSize: params.file.size,
        fileType: params.file.type,
        fileData,
        uploadDate: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        studentId: params.studentId,
        studentName: params.studentName
      };

      // Enhanced persistence - save to storage first with verification
      await enhancedDocumentStorage.addDocument(newDocument);
      console.log(`✅ Document saved to enhanced storage: ${newDocument.name}`);
      
      // Verify the document was actually saved by reloading
      const verificationDocuments = await enhancedDocumentStorage.loadDocuments();
      const savedDocument = verificationDocuments.find(doc => doc.id === newDocument.id);
      
      if (savedDocument) {
        console.log(`✅ Document persistence verified: ${savedDocument.name}`);
        
        // Update local state with verified documents
        setDocuments(verificationDocuments);
        
        if (params.studentId && params.studentName) {
          console.log(`✅ Enhanced student assignment verified: ${params.studentName} (ID: ${params.studentId})`);
        }
        
        return savedDocument;
      } else {
        throw new Error('Document not found after save - persistence failed');
      }
      
    } catch (error) {
      console.error('Enhanced document addition error:', error);
      // Try to reload documents to ensure consistency
      await loadDocuments();
      return null;
    }
  }, [loadDocuments]);

  // Delete document with immediate state update
  const deleteDocument = useCallback(async (documentId: string) => {
    try {
      console.log(`🗑️ Deleting document: ${documentId}`);
      
      // Remove from storage
      await enhancedDocumentStorage.deleteDocument(documentId);
      
      // Immediately update local state
      setDocuments(prevDocs => {
        const updatedDocs = prevDocs.filter(doc => doc.id !== documentId);
        console.log(`✅ Document deleted from state: ${documentId} (Remaining: ${updatedDocs.length})`);
        return updatedDocs;
      });
      
    } catch (error) {
      console.error('Error deleting document:', error);
      // Reload documents to ensure consistency
      await loadDocuments();
    }
  }, [loadDocuments]);

  const downloadDocument = useCallback((doc: EnhancedDocument) => {
    try {
      const link = document.createElement('a');
      link.href = doc.fileData;
      link.download = doc.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      console.log(`⬇️ Downloaded: ${doc.name}`);
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  }, []);

  const getDocumentsByCategory = useCallback((category: string) => {
    return documents.filter(doc => doc.category === category);
  }, [documents]);

  const getDocumentsByStudent = useCallback((studentId: string) => {
    const studentDocs = documents.filter(doc => doc.studentId === studentId);
    console.log(`📋 Found ${studentDocs.length} documents for student ID: ${studentId}`);
    return studentDocs;
  }, [documents]);

  const getStorageStats = useCallback(() => {
    return enhancedDocumentStorage.getStorageStats();
  }, []);

  // Enhanced initialization with persistence verification
  useEffect(() => {
    console.log('🚀 Enhanced useDocuments hook initializing with persistence checks...');
    loadDocuments();
  }, [loadDocuments]);

  // Enhanced page visibility change handler for persistence
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('👁️ Page visible - verifying document persistence...');
        refreshDocuments();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshDocuments]);

  return {
    documents,
    loading,
    addDocument,
    deleteDocument,
    downloadDocument,
    getDocumentsByCategory,
    getDocumentsByStudent,
    getStorageStats,
    refreshDocuments
  };
};
